import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import styled from 'styled-components'
import api from '../../../api'
import Responsive from '../../../assets/config/Responsive'
import Colors from '../../../assets/styles/Colors'
import Loader from '../../../components/Loader'
import BackgroundIcon from '../../../components/v2/BackgroundIcon'
import MovingPrice from '../../../components/v2/MovingPrice'
import ScreenHeader from '../../../components/v2/ScreenHeader'
import Title from '../../../components/v2/Title'
import Button from '../../../components/v2/buttons/Button'
import StartOver from '../../../components/v2/buttons/StartOver'
import PaymentCard from '../../../components/v2/cards/PaymentCard'
import BaseModal from '../../../components/v2/modals/BaseModal'
import { useAppDispatch, useAppSelector } from '../../../store'
import { AppState } from '../../../store/appSlice'
import { TravelAbroadState, clearAbroadStore } from '../../../store/travelAbroadSlice'
import { Helmet } from 'react-helmet-async'
//
//
const AbroadPayment = () => {
  //
  const dispatch = useAppDispatch()
  //
  const { t } = useTranslation()
  //
  const navigate = useNavigate()
  //
  const [startOverModal, setStartOverModal] = useState<boolean>(false)
  //
  const { isProduction } = useAppSelector((root: { app: AppState }) => root.app)
  //
  //
  const { checkout, filter } = useAppSelector((data: { travelAbroad: TravelAbroadState }) => data.travelAbroad)
  //
  // selected payment method
  const [selectedPaymentMethodState, setSelectedPaymentMethodState] = useState<string | undefined>(undefined)
  //
  const [getPaymentUrl, { data: paymentUrl, isLoading: isPaymentLoading }] = api.useLazyGetTravelPolicyPaymentUrlQuery()
  //
  //
  // look order id and payment method
  const _getAbroadPaymentUrlCallback = useCallback(async () => {
    //
    if (checkout.orderId && selectedPaymentMethodState && isProduction) {
      try {
        await getPaymentUrl({
          orderId: checkout.orderId,
          paymentType:
            selectedPaymentMethodState === 'master' && !filter.discountUsedSuccesfully
              ? selectedPaymentMethodState
              : 'other',
        }).unwrap()
      } catch (error: any) {
        if (error?.data?.errors?.constructor === Array) {
          const firstError = error.data.errors[0]
          const errorMessage = firstError.message
          toast.error(errorMessage)
        } else {
          toast.error(`${t('anErrorOccurred')} AbroadPayment.tsx`)
        }
      }
    }
  }, [checkout, getPaymentUrl, selectedPaymentMethodState, t, isProduction, filter.discountUsedSuccesfully])
  //
  //
  // get payment url
  useEffect(() => {
    _getAbroadPaymentUrlCallback()
    //
    return () => {}
  }, [_getAbroadPaymentUrlCallback])
  //
  // After Payment Steps On DEV
  const [afterPaymentStepsOnDev, { isLoading: isAfterPaymentStepsOnDevLoading }] =
    api.useAfterPaymentStepsOnDevMutation()
  //
  // main payment
  const mainPayment = () => {
    if (checkout.orderId && isProduction) {
      // got to checkout page
      if (paymentUrl && paymentUrl.url && isProduction) {
        // clear abroad rtk store
        dispatch(clearAbroadStore())
        //
        document.location.href = paymentUrl.url
      }
    }
  }
  //
  //
  // fake payment
  const fakePayment = async () => {
    if (checkout?.orderId && !isProduction) {
      //
      try {
        //
        await afterPaymentStepsOnDev({
          orderId: checkout?.orderId,
          amount: checkout?.totalPrice,
        }).unwrap()
        //
        toast.success(`${t('paymentSuccessfully')}`)
        //
        // clear abroad rtk store
        dispatch(clearAbroadStore())
        //
        //
        navigate('/pay/payment-success')
        //
      } catch (error: any) {
        if (error?.data?.errors?.constructor === Array) {
          const firstError = error.data.errors[0]
          const errorMessage = firstError.message
          toast.info(errorMessage)
          //
        } else {
          toast.error('შეცდომა გადახდის გვერდზე, Payment.tsx')
        }
      }
      //
    }
  }
  //
  // disable browser back button
  useEffect(() => {
    const disableBackButton = (e: any) => {
      e.preventDefault()
      window.history.forward()
    }

    window.history.pushState('', '', window.location.href)
    window.addEventListener('popstate', disableBackButton)

    return () => {
      window.removeEventListener('popstate', disableBackButton)
    }
  }, [])
  //
  //
  const MainLoading = isPaymentLoading || isAfterPaymentStepsOnDevLoading
  //
  //
  return MainLoading ? (
    <Loader text={t('redirectionToPayment')} />
  ) : (
    <Wrapper>
      <Helmet>
        <title>{t('chooseCard')}</title>
        <meta name="description" content={`${t('chooseCard')}`} />
        <link rel="canonical" href={window.location.href} />
      </Helmet>
      <BaseModal isOpen={startOverModal} onClose={() => setStartOverModal(!startOverModal)}>
        <StartOverDiv>
          <h5>{t('areyouSureTostartOver')}</h5>
          <ButtonDiv>
            <Button
              fullWidth={true}
              text={t('yes')}
              buttonType="primary"
              onClick={() => [dispatch(clearAbroadStore()), navigate('/single-select-date')]}
            />
            <Button
              fullWidth={true}
              text={t('no')}
              buttonType="transparent"
              onClick={() => setStartOverModal(!startOverModal)}
            />
          </ButtonDiv>
        </StartOverDiv>
      </BaseModal>

      <ScreenHeader
        rightSideChildren={<StartOver text={t('startOver')} onClick={() => setStartOverModal(!startOverModal)} />}
      />

      <MovingPrice price={filter.offerTotalPrice} />

      <Content>
        <Title text={t('chooseCard')} />

        <Cards>
          {['master', 'visa'].map((item, index) => {
            return (
              <PaymentCard
                key={index}
                cardName={item === 'master' ? 'Mastercard' : 'Visa'}
                selected={item === selectedPaymentMethodState}
                cardType={item}
                saleCount={
                  checkout.orderId === 0
                    ? undefined
                    : !filter.discountUsedSuccesfully && item === 'master'
                    ? 50
                    : undefined
                }
                onClick={() => {
                  if (selectedPaymentMethodState === item) {
                    setSelectedPaymentMethodState(undefined)
                  } else {
                    setSelectedPaymentMethodState(item)
                  }
                }}
              />
            )
          })}

          {selectedPaymentMethodState && checkout && checkout.orderId ? (
            <Button
              fullWidth={false}
              style={{
                marginTop: '48px',
              }}
              text={t('pay')}
              buttonType="primary"
              onClick={() => {
                //
                // main payment
                if (checkout && checkout.orderId && isProduction) {
                  mainPayment()
                }
                // fake payment
                if (checkout && checkout.orderId && !isProduction) {
                  fakePayment()
                }
              }}
            />
          ) : null}
        </Cards>
      </Content>

      <BackgroundIcon />
    </Wrapper>
  )
}
//
const ButtonDiv = styled.div`
  margin-top: 50px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: 1fr;
  grid-column-gap: 20px;
  grid-row-gap: 0px;

  ${Responsive.mobile} {
    grid-template-columns: repeat(2, 130px);
    justify-content: center;
  }
`
const StartOverDiv = styled.div`
  h5 {
    text-align: center;
    color: ${Colors.primaryBlack};
  }
`
const Cards = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  margin-block: 32px;
`

const Content = styled.div`
  margin-top: 58px;
  z-index: 2;
  padding-inline: 20px;
  width: 100%;

  ${Responsive.mobile} {
    margin-top: 26px;
  }
`
const Wrapper = styled.div`
  display: flex;
  justify-content: start;
  align-items: center;
  flex-direction: column;
  background-color: ${Colors.backgroundGrey};
  padding-bottom: 20px;
  min-height: 100vh;
`
//
export default AbroadPayment
